import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Axios response interceptor (una sola volta, a livello di modulo) ───────
// Normalizza `error.response.data.detail` quando è un ARRAY di errori Pydantic v2
// (oggetti con chiavi {type, loc, msg, input, url}) in una stringa user-friendly.
// Previene React error #31 quando .detail viene renderizzato in JSX/toast.
if (!axios.__jvservice_error_interceptor) {
  axios.__jvservice_error_interceptor = true;
  axios.interceptors.response.use(
    (resp) => resp,
    (error) => {
      // ─── Auto-logout su 401 (token scaduto/non valido) ───
      try {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const url = error?.config?.url || '';
        const isAuthCheck = url.includes('/api/auth/login') || url.includes('/api/auth/register') || url.includes('/api/auth/2fa');
        if (status === 401 && !isAuthCheck && !window.__jvservice_logging_out) {
          window.__jvservice_logging_out = true;
          const detailStr = typeof detail === 'string' ? detail.toLowerCase() : '';
          const isExpired = detailStr.includes('scaduto') || detailStr.includes('expired') || detailStr.includes('invalid');
          const msg = isExpired
            ? '⏰ Sessione scaduta. Effettua nuovamente il login.'
            : '🔒 Non autorizzato. Effettua il login.';
          // Pulisci token + redirect
          try { localStorage.removeItem('token'); } catch (_) {}
          try { delete axios.defaults.headers.common['Authorization']; } catch (_) {}
          try { sessionStorage.setItem('jvservice_login_redirect_msg', msg); } catch (_) {}
          // Reindirizza solo se non siamo già sul login
          if (!window.location.pathname.startsWith('/login')) {
            setTimeout(() => {
              window.location.href = '/login';
            }, 400);
          } else {
            window.__jvservice_logging_out = false;
          }
        }
      } catch (e) {
        console.error('[axios interceptor] 401 handler failed', e);
      }

      // ─── Salvataggio dettaglio completo per debug ───
      try {
        const dump = {
          timestamp: new Date().toISOString(),
          method: error?.config?.method?.toUpperCase(),
          url: error?.config?.url,
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          requestPayload: (() => {
            try { return error?.config?.data ? JSON.parse(error.config.data) : null; } catch (e) { return error?.config?.data; }
          })(),
          responseBody: error?.response?.data,
          message: error?.message,
        };
        const history = JSON.parse(localStorage.getItem('jvservice_api_errors') || '[]');
        history.unshift(dump);
        localStorage.setItem('jvservice_api_errors', JSON.stringify(history.slice(0, 10)));
        // Notifica eventuali listener (es. DebugErrorButton)
        try { window.dispatchEvent(new CustomEvent('jvservice:api-error', { detail: dump })); } catch (_) {}
      } catch (e) {
        console.error('[axios interceptor] dump failed', e);
      }

      // ─── Normalizzazione detail per evitare React error #31 ───
      try {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          // Pydantic v2 ValidationError array
          const formatted = detail.map((d) => {
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object') {
              const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
              const msg = d.msg || d.message || 'errore';
              return field ? `${field}: ${msg}` : msg;
            }
            return String(d);
          }).join('; ');
          error.response.data.detail = formatted;
        } else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
          if (detail.msg) {
            error.response.data.detail = String(detail.msg);
          } else {
            try {
              error.response.data.detail = JSON.stringify(detail);
            } catch (e) {
              error.response.data.detail = 'Errore di validazione';
            }
          }
        }
      } catch (e) {
        console.error('[axios interceptor] normalization failed', e);
      }
      return Promise.reject(error);
    }
  );
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const fetchingRef = useRef(false);

  const fetchUser = useCallback(async (currentToken) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      setUser(response.data);
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on 401 (unauthorized), not on network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
      // For other errors (network, 500, etc.), keep the token and user state
      // This prevents random logouts on temporary network issues
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      fetchUser(storedToken);
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, []); // Run only once on mount

  // Handle token changes from login/logout
  useEffect(() => {
    if (initialized && token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token, initialized]);

  // Salva la sessione (token pieno + utente) dopo un login completato.
  const applySession = ({ access_token, user: userData }) => {
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Ritorna la risposta grezza del backend. Se contiene access_token, la sessione
  // è già applicata; altrimenti il chiamante gestisce il 2° fattore (mfa_required
  // / enrollment_required + mfa_token) tramite <TwoFactorChallenge>.
  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data?.access_token) applySession(response.data);
    return response.data;
  };

  const register = async (email, password, full_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name });
    if (response.data?.access_token) applySession(response.data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasModuleAccess = (module) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    return user.modules_access?.includes(module) || false;
  };

  const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = () => user?.role === 'superadmin';
  // Livello "master": additivo al ruolo, non lo sostituisce. Gate del modulo Agenti.
  const isMaster = () => user?.is_master === true;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, applySession, hasModuleAccess, isAdmin, isSuperAdmin, isMaster }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
